import { computed, ref, reactive } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import { cloneDeep, isEmpty, pick } from 'lodash'
import { events$ } from '@shared/services'
import { createDistributionPartners } from '@shared/helpers/Partner'
import { SOFT_CLOSED_PRODUCTS } from '@shared/utils/HaX'
import { getAllContractStartDates } from '@shared/utils/BasketConfig'

import { getNewContractStartDate, isInvalidContractStartDate } from '@shared/modules/PartnerWidget/helpers/dates'
import {
  BOOLEAN_CONFIG_KEYS,
  CHANNEL,
  SCREEN,
  EVENT_PARTNER_WIDGET,
  PARTNER_WIDGET_RESTART_TIMEOUT,
} from '@PartnerWidget/constants'
import { GENDER, NOTIFICATION, ROUTE_NAME } from '@shared/config/constants'
import { EVENT_TRACKING } from '@shared/config/events'

import masterDataAPI from '@shared/api/masterData'
import partnerWidgetAPI from '@pawi/api/partnerWidget'
import productAPI from '@shared/api/product'
import basketStore from '@shared/store/basket'
import useSnackbar from '@shared/hooks/useSnackbar'
import useI18n from '@shared/hooks/useI18n'

import { ReactivityUtil } from '@shared/utils/Reactivity'

// GLOBAL DATA
const bundleId = ref('')
const defaultKVGContractStart = ref()
const hasChosenPackage = ref(false)
const ready = ref(false)
const screen = ref(SCREEN.ENTRY)
const screensDefinition = ref({})
const customerTheme = ref('')
const code = ref('')
const bundleConfig = reactive({})
const packages = reactive([])

export default function usePartnerWidgetBundleLoader() {
  // HOOKS
  const { addNotification } = useSnackbar()
  const { selectedLanguage, t } = useI18n()

  // COMPUTED
  const theme = computed(() => {
    if (customerTheme.value) {
      return customerTheme.value
    }
    if (Object.keys(bundleConfig).length != 0) {
      return bundleConfig.theme
    }
    return 'light'
  })

  const getDefaultKVGContractStart = async () => {
    defaultKVGContractStart.value =
      defaultKVGContractStart.value ?? (await getNewContractStartDate()).kvgContractStartDate
    return defaultKVGContractStart.value
  }

  // METHODS
  function screensDefinitionInit() {
    screensDefinition.value = {
      [SCREEN.ENTRY]: {
        events: {
          enter: enter,
        },
        props: {},
      },
      [SCREEN.BUNDLE]: {
        events: {
          requireDetail: onDetailRequired,
        },
        props: {
          logo: false,
          packages: [],
          skipEntryPage: false,
          showPackagesOverviewInfoIcon: false,
        },
      },
      [SCREEN.DETAIL]: {
        events: {
          backToBundle: onBackToBundle,
          editScreenDefinitions: editScreenDefinitions,
          goToSanitas: goToSanitas,
        },
        props: {
          item: null,
          skipEntryPage: false,
          skipPackagesOverview: false,
          openInSameWindow: false,
        },
      },
      [SCREEN.ERROR]: {
        props: {
          messages: [],
          language: selectedLanguage.value,
        },
      },
    }
  }

  async function loadBundle(params) {
    try {
      const bundleResponse = await partnerWidgetAPI.getBundle(bundleId.value, selectedLanguage.value)
      code.value = bundleResponse.code

      ReactivityUtil.reAssign(bundleConfig, overrideBundleWithUrlProps(bundleResponse))
      ReactivityUtil.resetData(packages)

      for (const bundleBasket of bundleResponse.baskets) {
        let location = params.location
        if (location) {
          const contractStartDate = bundleBasket.contractStartDate ?? (await getDefaultKVGContractStart())
          location = await findLocation(location, contractStartDate)
        }

        // HaX Filter out all Classic, Family and Jump products
        for (const __package of bundleBasket.packages) {
          __package.products = __package.products.filter(p => {
            return !SOFT_CLOSED_PRODUCTS.includes(p.id)
          })
        }

        // Filter out all packages with no products in it
        bundleBasket.packages = bundleBasket.packages.filter(pack => {
          return pack.products.length
        })

        for (const __package of bundleBasket.packages) {
          let updatedBasketBundle = await overrideBasketBundleWithUrlProps(
            cloneDeep(bundleBasket),
            Object.assign({}, params, { location })
          )

          delete updatedBasketBundle.packages
          updatedBasketBundle = await createBasket(__package, updatedBasketBundle)
          const productsStructure = await loadProducts({
            persons: updatedBasketBundle.persons,
            collective: updatedBasketBundle?.collective,
            masterTreaty: updatedBasketBundle.masterTreaty,
          })
          const packageData = {
            ...__package,
            basket: updatedBasketBundle,
            productsStructure,
            showExampleOfferText: true,
            isPersonDataEdited: false,
          }
          packages.push(packageData)
        }
      }

      // general
      loadThemeCSS()
      setScreenDefinitions()

      if (bundleConfig.skipEntryPage) {
        enter()
      }
    } catch (/** @type {ErrorResponse}*/ e) {
      console.error(e)
      if (e.messages) {
        screensDefinition.value[SCREEN.ERROR].props.messages = e.messages
      }
      screen.value = SCREEN.ERROR
    }
    ready.value = true
  }

  async function createBasket(__package, basket) {
    basket.basketId = __package?.id || uuidv4()

    const payloadBasket = getBasketPayload(basket)
    const packageProducts = __package.products
    const updatedBasket = cloneDeep(payloadBasket)
    updatedBasket.persons[0].products = { products: {} }
    for (const product of packageProducts) {
      const prices = []
      prices.push({
        reductions: [],
        toggle: product.toggle ?? undefined,
        value: product.value ?? undefined,
        selected: true,
      })
      updatedBasket.persons[0].products.products[product.id] = {
        conditionalProduct: false,
        prices,
        recommended: false,
        selected: true,
        showDiscountGroup: false,
        valid: true,
      }
    }

    return await basketStore.updateOvpBasket(updatedBasket, { simulate: true })
  }

  // find matching location passed via url params
  async function findLocation(params, contractStartDate) {
    const query = params?.zipCode
    const locations = await masterDataAPI.getLocations({
      query,
      referenceDate: contractStartDate
    }).catch(e => {
      console.warn('location service not available', e)
    })

    const locationData = locations?.data

    if (!locationData || locationData.length === 0) {
      return null
    }

    if (locationData.length === 1) {
      return locationData[0]
    }

    const matchingLocation = locationData.find(location => {
      const locationZipAddon = parseInt(location.zipCodeAddon, 10)
      const locationLocationNumber = parseInt(location.locationNumber, 10)

      const paramZipAddon = parseInt(params.zipCodeAddon, 10)
      const paramLocationNumber = parseInt(params.locationNumber, 10)

      return locationZipAddon === paramZipAddon && locationLocationNumber === paramLocationNumber
    })

    // fall back to first location for zipCode, if zipAddon or locationNumber don't match
    return matchingLocation ?? locationData[0]
  }

  /**
   * Updates the products in the basket (options with accident)
   * If selectProducts is true, the products are selected as well
   */
  function updateProductsInBasket(__basket, products, selectProducts = false, simulate = true) {
    const packageProducts = products
    const updatedBasket = cloneDeep(__basket)
    const personProducts = updatedBasket.persons[0].products?.products

    resetProductSelection(personProducts)
    applyCriteriaAndSelectProducts(personProducts, packageProducts, selectProducts)

    return basketStore.updateOvpBasket(updatedBasket, { simulate })
  }

  function resetProductSelection(personProducts) {
    // reset selection for products
    Object.values(personProducts).forEach(product => {
      product.selected = false
      product.prices.map(price => {
        price.selected = false
        return price
      })
      return product
    })
  }

  function applyCriteriaAndSelectProducts(personProducts, packageProducts, selectProducts) {
    packageProducts.forEach(p => {
      const { id, toggle, value } = p

      // select product
      const basketProduct = personProducts[p.id]
      if (!basketProduct) {
        return
      }

      const priceOptions = basketProduct.prices

      if (selectProducts) {
        basketProduct.selected = true
      }

      const selectedPriceOption = setSelectedPriceOption(priceOptions, value)
      setToggleAndSelectPriceOption(priceOptions, selectedPriceOption, toggle)

      // select product, set value & toggle
      if (selectProducts) {
        // reset selections for priceOptions
        priceOptions.forEach(price => {
          price.selected = false
        })
        if (selectedPriceOption) {
          selectedPriceOption.selected = true
        } else {
          console.error('Can not select correct price', id, toggle, value)
        }
      }
    })
  }

  function setSelectedPriceOption(priceOptions, value) {
    return priceOptions.length > 1
      ? priceOptions.find(price => {
          // check if value defined, value can be undefined for e.g. AB04Produkt
          const isPriceMatching = value ? price.value === parseInt(value, 10) : true
          const isDurationMatching = price.duration ? price.duration === '1' : true
          return isPriceMatching && isDurationMatching
        })
      : priceOptions[0]
  }

  // set toggle value and select this price option. Other toggle values will be set by backend
  function setToggleAndSelectPriceOption(priceOptions, selectedPriceOption, toggle) {
    if (selectedPriceOption && toggle !== undefined) {
      selectedPriceOption.toggle = toggle
      priceOptions.forEach(option => {
        option.selected = false
      })
      selectedPriceOption.selected = true
    }
  }

  function getBasketPayload(bundleBasket) {
    return {
      basketId: bundleBasket.basketId,
      createDate: new Date().toISOString(),
      channel: CHANNEL.PARTNER_WIDGET,
      collective: isEmpty(bundleBasket.collective) ? null : bundleBasket.collective,
      masterTreaty: bundleBasket.masterTreaty,
      language: selectedLanguage.value.toUpperCase(),
      partnerWidgetBundleCode: code.value,
      distributionPartners: createDistributionPartners(bundleBasket.distributionPartnerNumber),
      contractStartDate: bundleBasket.contractStartDate,
      address: bundleBasket.address,
      route: ROUTE_NAME.BASKET,
      persons: [
        {
          collectivePersonGroup: bundleBasket.collectivePersonGroup,
          personId: uuidv4(),
          immigrant: false,
          personData: {
            dateOfBirth: bundleBasket.person.dateOfBirth,
            firstName: bundleBasket.person.firstName,
            lastName: null,
            gender: bundleBasket.person.gender,
          },
          kvgContractStartDate: bundleBasket.contractStartDate,
          vvgContractStartDate: bundleBasket.contractStartDate,
          preInsurer: {},
        },
      ],
    }
  }

  async function loadProducts(__basket) {
    // use basket from backend response, contractStartdate may be set by BE
    const startDates = getAllContractStartDates(__basket)
    const payload = {
      collectiveChannel: __basket.collective?.channel,
      collectiveContractNumber: __basket.collective?.contractNumber,
      contractStartDates: Array.from(startDates),
      masterTreaty: __basket.masterTreaty,
    }
    return await productAPI.getProducts(payload).then(products => {
      return products
    })
  }

  function overrideBundleWithUrlProps(__bundle) {
    const { config: __config } = __bundle
    // parse boolean __configuration values from url
    BOOLEAN_CONFIG_KEYS.forEach(key => {
      if (__config[key]) {
        __config[key] = __config[key] === 'true' || __config[key]
      }
    })

    return __config
  }

  async function overrideBasketBundleWithUrlProps(bundleBasket, __options) {
    // Set current date if bundle contract start date are not valid
    if (
      !bundleBasket.contractStartDate ||
      (bundleBasket.contractStartDate && isInvalidContractStartDate(bundleBasket.contractStartDate))
    ) {
      bundleBasket.contractStartDate = await getDefaultKVGContractStart()
    }
    // Set location if valid
    if (!isEmpty(__options.location)) {
      bundleBasket.address = __options.location
    }
    // Set gender if given gender available
    if ([GENDER.MALE, GENDER.FEMALE, GENDER.UNBORN].includes(__options.gender)) {
      bundleBasket.person.gender = __options.gender
    }

    if (__options.firstName) {
      bundleBasket.person.firstName = __options.firstName
    }

    // Set dateOfBirth
    if (__options.dateOfBirth) {
      bundleBasket.person.dateOfBirth = __options.dateOfBirth
    }

    return bundleBasket
  }

  function setScreenDefinitions() {
    screensDefinition.value[SCREEN.ENTRY].props = Object.assign(
      {},
      screensDefinition.value[SCREEN.ENTRY].props,
      pick(bundleConfig, ['logo', 'badge']),
      { basketAvailable: !!packages[0]?.basket?.basketId }
    )
    screensDefinition.value[SCREEN.BUNDLE].props = Object.assign(
      {},
      screensDefinition.value[SCREEN.BUNDLE].props,
      pick(bundleConfig, ['logo', 'skipEntryPage', 'showPackagesOverviewInfoIcon']),
      { packages }
    )
    screensDefinition.value[SCREEN.DETAIL].props = Object.assign({}, screensDefinition.value[SCREEN.DETAIL].props, {
      skipEntryPage: bundleConfig.skipEntryPage,
      skipPackagesOverview: bundleConfig.skipPackagesOverview,
      openInSameWindow: bundleConfig.openInSameWindow,
    })
  }

  function editScreenDefinitions(newScreenDefinitionsProps) {
    Object.assign(screensDefinition.value[SCREEN.DETAIL].props.item, newScreenDefinitionsProps)
  }

  function addStylesheet(url) {
    const themeFile = document.createElement('link')
    themeFile.setAttribute('rel', 'stylesheet')
    themeFile.setAttribute('type', 'text/css')

    themeFile.setAttribute('href', url)
    window.document.head.appendChild(themeFile)
  }

  function loadThemeCSS() {
    if (theme.value === 'custom') {
      // never show logo if custom theme is used
      bundleConfig.logo = false
      partnerWidgetAPI
        .getCSSForBundle(code.value, bundleConfig.lastModifiedDate)
        .then(url => {
          addStylesheet(url)
        })
        .catch(e => {
          console.error(e)
        })
    }
  }

  function enter() {
    if (bundleConfig.skipPackagesOverview) {
      onDetailRequired(packages[0])
    } else {
      goToBundle()
    }
  }

  function onDetailRequired(pack) {
    screensDefinition.value[SCREEN.DETAIL].props.item = pack
    screen.value = SCREEN.DETAIL
  }

  function goToBundle() {
    screen.value = SCREEN.BUNDLE
  }

  function onBackToBundle() {
    screen.value = SCREEN.BUNDLE
  }

  async function goToSanitas(options) {
    if (hasChosenPackage.value) {
      return
    }

    events$.emit(EVENT_PARTNER_WIDGET.GOTO_SANITAS)

    try {
      const { item, target } = options
      hasChosenPackage.value = true
      // don't update store! Kvg without doctor can set the valid flag on the product to false,
      // causing the chosen package to be displayed as unavailable!
      let realBasket = cloneDeep(item.basket)
      delete realBasket.createDate
      delete realBasket.basketId
      realBasket = await updateProductsInBasket(realBasket, item.products, true, false)

      events$.emit(EVENT_TRACKING.ENTERED, { source: CHANNEL.PARTNER_WIDGET })

      // Create the URL for the DVP application
      const dvpUrl = `${import.meta.env.VITE_APP_ENTRY_ENV}/${realBasket.language.toLowerCase()}/${realBasket.reentryToken}`

      // Open the DVP in a new tab/window
      try {
        window.open(dvpUrl, target)
      } catch (error) {
        console.error('Failed to open DVP:', error)
      }

      // Show notification to the user with option to retry if popup was blocked
      addNotification({
        text: t('partnerWidget.popupMessage'),
        type: NOTIFICATION.WARNING,
        action: {
          label: t('partnerWidget.updateOffer'),
          callback: () => {
            try {
              window.open(dvpUrl, target)
            } catch (error) {
              console.error('Failed to open DVP from notification:', error)
            }
          },
        },
      })

      // wait before reloading the widget in case a popup has prevented the new tab from opening
      // due to the navigation, the popup blocker message disappears instantly otherwise.
      window.setTimeout(() => {
        restartWidgetWithData(item.basket)
      }, PARTNER_WIDGET_RESTART_TIMEOUT)
    } catch (e) {
      hasChosenPackage.value = false
      console.error(e)
    }
  }

  function restartWidgetWithData(basket) {
    const parsedUrl = new URL(window.location.href)
    const searchParams = parsedUrl.searchParams

    const { address, persons } = basket
    const firstPersonData = persons[0].personData

    // update search params with entered data
    searchParams.set('gender', firstPersonData.gender)
    searchParams.set('birthDate', firstPersonData.dateOfBirth)

    const { locationNumber, zipCode, zipCodeAddon } = address
    searchParams.set('locationNumber', locationNumber)
    searchParams.set('zipCode', zipCode)
    searchParams.set('zipCodeAddon', zipCodeAddon)

    // restart widget
    location.search = searchParams.toString()
  }

  // Setter methods
  function setBundleId(value) {
    bundleId.value = value
  }

  function setCustomerTheme(value) {
    customerTheme.value = value
  }

  return {
    // Data
    bundleConfig,
    bundleId,
    hasChosenPackage,
    ready,
    screen,
    screensDefinition,
    screensDefinitionInit,
    theme,

    // Methods
    loadBundle,

    // Setter Methods
    setBundleId,
    setCustomerTheme,
  }
}
